/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import { Document } from 'flexsearch';
import SearchComponent, { Suggestion, SearchComponentProps } from './SearchComponent';
import { searchInitiativeByName } from '../../actions/initiative';
import Loader from '../loader';
import {
  InitiativeSuggestion,
  InitiativeSuggestionState
} from '../../constants/initiative';
import Avatar from '../avatar';

const renderSuggestion = (suggestion: Partial<InitiativeSuggestion>) => {
  return (
    <div className='suggestion-item'>
      <Avatar width='30px'>
        {suggestion.profile ? <img alt='avatar' src={suggestion.profile} /> : <></>}
      </Avatar>
      <span>
        <div><strong>{suggestion.name}</strong></div>
      </span>
    </div>
  );
}

interface InitiativeSearchParams extends Partial<SearchComponentProps> {
  options?: InitiativeSuggestion[];
  filterOptions?: string[];
  searchInitiative?: () => Promise<InitiativeSuggestion[]>;
  name?: string;
  placeholder: string;
  disabled?: boolean;
  autoComplete?: string;
  handleValueChange: (value: string, currentSuggestion?: Suggestion) => void;
  clearMessageCallback?: () => void;
}

export const InitiativeSearch = (props: InitiativeSearchParams) => {
  const { searchInitiative = searchInitiativeByName } = props;

  const [{ loaded, initiatives }, setSuggestions] = useState<InitiativeSuggestionState>({
    loaded: !!props.options,
    initiatives: props.options ?? [],
  })

  useEffect(() => {
    let mounted = true;
    if (!loaded) {
      searchInitiative()
        .then((d: InitiativeSuggestion[]) => mounted && setSuggestions({ loaded: true, initiatives: d }))
        .catch(() => mounted && setSuggestions({ loaded: true, initiatives: [] }))
    }
    return function cleanup() {
      mounted = false;
    }
  }, [searchInitiative, loaded])

  if (!loaded) {
    return <Loader />
  }

  const searchAction = (searchStr: string): Promise<Suggestion[]> => {
    const initiativesMap = new Map<string, Suggestion>();
    const indexSearch = new Document({
      tokenize: 'forward',
      preset: 'performance',
      document: {
        id: '_id',
        index: [
          'name',
          'code',
          'permissionGroup'
        ]
      }
    });
    
    (initiatives ?? []).forEach(initiative => {
      indexSearch.add(initiative);
      initiativesMap.set(initiative._id, initiative);
    });
    const foundIndex = indexSearch.search(searchStr, { index: 'name' });
    const foundResult = foundIndex[0]?.result ?? [];
  
    return Promise.resolve(foundResult.map((initiativeId) => initiativesMap.get(String(initiativeId)) as Suggestion));
  }

  return (
    <SearchComponent
      {...props}
      required={true}
      inputClassName='form-control'
      handleValueChange={props.handleValueChange}
      searchAction={searchAction}
      renderSuggestion={renderSuggestion}
    />
  );
};

